// Diagnostic probe for the websocket health loop.
//
// Answers two questions that the existing `ws-logger` cannot, because it
// filters `health` messages out before recording:
//
//   1. On an idle channel, does ANYTHING arrive from the server that is not
//      our own health echo? This decides whether the app-level health ping
//      can be replaced by a receive-side idle timer (zero outbound traffic)
//      or whether a low-rate ping has to stay.
//
//   2. When the "Reconnecting..." banner appears, did the SDK actually report
//      a disconnect -- or is the health loop showing trouble while the socket
//      is fine? `checkHealth` stops sending pings once it misses one, so a
//      single lost echo pins the banner on until Scaledrone happens to
//      reconnect underneath it.
//
// Recording is event-driven, never timer-driven, so background-tab timer
// throttling cannot distort the record itself.
//
// Gated behind the sticky `?debug` flag. Nothing here changes socket
// behavior; it only observes.

import { isDebugEnabled } from '../embed/lib/debugFlag';

const MAX_ENTRIES = 5000;

const entries = [];
// sessionIds this tab has published under, and the sessionId:iteration pairs
// already timed -- the same echo arrives once per registered handler.
const ours = new Set();
const seen = new Set();
const rtts = [];
let enabled = false;
let startedAt = null;

/** Monotonic ms since the probe started, immune to wall-clock adjustment. */
function elapsed () {
    if (startedAt === null) return 0;
    return Math.round(now() - startedAt);
}

function now () {
    return (typeof performance !== 'undefined' && performance.now)
        ? performance.now()
        : Date.now();
}

/** Ms since the last entry of the same kind, or null if this is the first. */
function gapSince (kind) {
    for (let i = entries.length - 1; i >= 0; i--) {
        if (entries[i].kind === kind) return elapsed() - entries[i].at;
    }
    return null;
}

function record (kind, detail) {
    if (!enabled) return;

    const entry = {
        at: elapsed(),
        wall: new Date().toISOString(),
        kind,
        gap: gapSince(kind),
        detail: detail || null
    };

    entries.push(entry);
    if (entries.length > MAX_ENTRIES) entries.shift();

    const secs = (entry.at / 1000).toFixed(1);
    const gap = entry.gap === null ? '-' : (entry.gap / 1000).toFixed(1) + 's';
    console.log(`[wsProbe ${secs}s] ${kind} (+${gap})`, detail || '');

    return entry;
}

/**
 * Every inbound room message, BEFORE the health filter in SocketAdapter.
 * Health echoes are separated out because they only exist while we are
 * paying for the outbound ping -- they cannot count as evidence that a
 * receive-side timer would have something to listen to.
 */
function inbound (ev) {
    if (!enabled) return;

    const action = (ev && ev.action) || {};
    const isHealth = action.method === 'health';
    const data = (ev && ev.data) || {};

    if (!isHealth) {
        return record('data', {
            method: action.method || null,
            type: action.type || null
        });
    }

    // Ours or someone else's? The room carries every operator's health
    // traffic, and only our own echoes can be timed -- a foreign session's
    // `timestamp` came off a different machine's clock.
    const mine = ours.has(data.sessionId);
    const key = data.sessionId + ':' + data.socketIteration;

    // The same echo arrives once per registered `data` handler, so a
    // duplicate is a second delivery of one round trip, not a second sample.
    const dupe = mine && seen.has(key);
    if (mine && !dupe) {
        seen.add(key);
        if (typeof data.timestamp === 'number') rtts.push(Date.now() - data.timestamp);
    }

    record('health-echo', {
        method: action.method || null,
        type: action.type || null,
        mine: mine,
        dupe: dupe,
        rttMs: (mine && !dupe && typeof data.timestamp === 'number')
            ? Date.now() - data.timestamp
            : null
    });
}

/**
 * Called by the health loop as it publishes, so the probe knows which
 * sessions are its own and can time their echoes. Without this the probe
 * cannot tell our traffic from any other operator's in the same room.
 */
function outbound (sessionId, iteration) {
    if (!enabled) return;
    ours.add(sessionId);
    record('health-ping', { sessionId: sessionId, iteration: iteration });
}

/** Round-trip percentiles over our own echoes only, duplicates excluded. */
function rtt () {
    if (!rtts.length) return { samples: 0 };
    const s = rtts.slice().sort((a, b) => a - b);
    const at = q => s[Math.min(s.length - 1, Math.floor(q * s.length))];
    return {
        samples: s.length,
        minMs: s[0],
        medianMs: at(0.5),
        p95Ms: at(0.95),
        p99Ms: at(0.99),
        maxMs: s[s.length - 1]
    };
}

/** Scaledrone lifecycle: open, close, error, disconnect, reconnect. */
function sdk (name, detail) {
    record('sdk:' + name, detail);
}

/** The health loop's own verdict -- what drives the banner. */
function banner (state) {
    record('banner:' + state);
}

function stats (kind) {
    const gaps = entries
        .filter(e => e.kind === kind && e.gap !== null)
        .map(e => e.gap);

    if (!gaps.length) return { count: entries.filter(e => e.kind === kind).length, gaps: null };

    const sorted = gaps.slice().sort((a, b) => a - b);
    return {
        count: entries.filter(e => e.kind === kind).length,
        maxGapSec: +(sorted[sorted.length - 1] / 1000).toFixed(1),
        medianGapSec: +(sorted[Math.floor(sorted.length / 2)] / 1000).toFixed(1),
        minGapSec: +(sorted[0] / 1000).toFixed(1)
    };
}

/**
 * The answer to the question. Read `data` -- if its count is 0, or its
 * maxGapSec is longer than any timeout you would be willing to wait, a
 * receive-side idle timer has nothing to listen to on a quiet channel.
 */
function summary () {
    const out = {
        ranForSec: +(elapsed() / 1000).toFixed(1),
        data: stats('data'),
        healthEcho: stats('health-echo'),
        roundTrip: rtt(),
        sdkEvents: entries.filter(e => e.kind.indexOf('sdk:') === 0)
            .map(e => `${(e.at / 1000).toFixed(1)}s ${e.kind.slice(4)}`),
        bannerEvents: entries.filter(e => e.kind.indexOf('banner:') === 0)
            .map(e => `${(e.at / 1000).toFixed(1)}s ${e.kind.slice(7)}`)
    };
    console.table([out.data, out.healthEcho].filter(Boolean));
    console.log('wsProbe summary', out);
    return out;
}

function download (filename) {
    const name = filename || `ws-probe-${new Date().toISOString().replace(/[:.]/g, '-')}.jsonl`;
    const body = entries.map(e => JSON.stringify(e)).join('\n') + '\n';
    const blob = new Blob([body], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return name;
}

function start () {
    enabled = true;
    startedAt = now();
    entries.length = 0;
    ours.clear();
    seen.clear();
    rtts.length = 0;
    console.log('[wsProbe] recording. Leave the tab OPEN AND VISIBLE. ' +
        'Call wsProbe.summary() when done.');
}

function stop () {
    enabled = false;
}

const probe = {
    start, stop, summary, download, stats, rtt,
    entries,
    get isRecording () { return enabled; }
};

// Internal hooks, called from the socket layer. No-ops unless started.
probe.inbound = inbound;
probe.outbound = outbound;
probe.sdk = sdk;
probe.banner = banner;

if (typeof window !== 'undefined' && isDebugEnabled()) {
    window.wsProbe = probe;
    start();
}

export default probe;
